import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getMe, logoutUser } from "@/lib/auth";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import TeamPage from "@/pages/TeamPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import Dashboard from "@/pages/Dashboard";
import ProductRatesPage from "@/pages/ProductRatesPage";
import AttendancePage from "@/pages/AttendancePage";
import InventoryPage from "@/pages/InventoryPage";
import CustomersPage from "@/pages/CustomersPage";
import PurchasePage from "@/pages/PurchasePage";
import AccountsPage from "@/pages/AccountsPage";
import SalesDashboardPage from "@/pages/SalesDashboardPage";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthContext, canAccessPath, homePath } from "@/lib/access";

const ROUTES = [
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/my-dashboard", element: <SalesDashboardPage /> },
  { path: "/leads", element: <LeadsPage /> },
  { path: "/team", element: <TeamPage /> },
  { path: "/reports", element: <ReportsPage /> },
  { path: "/product-rates", element: <ProductRatesPage /> },
  { path: "/attendance", element: <AttendancePage /> },
  { path: "/inventory", element: <InventoryPage /> },
  { path: "/customers", element: <CustomersPage /> },
  { path: "/purchase", element: <PurchasePage /> },
  { path: "/settings", element: <SettingsPage /> },
  { path: "/expenses", element: <AccountsPage /> },
];

function AppRoutes({ user, onAuth, onLogout }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback onAuth={onAuth} />;
  return (
    <Routes>
      {!user ? (
        <Route path="*" element={<AuthPage onAuth={onAuth} />} />
      ) : (
        <>
          <Route element={<Layout user={user} onLogout={onLogout} />}>
            {ROUTES.filter((r) => canAccessPath(user, r.path)).map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
          </Route>
          <Route path="*" element={<Navigate to={homePath(user)} replace />} />
        </>
      )}
    </Routes>
  );
}

// Keep all open tabs on the same account: cookies are shared per browser
const authChannel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("huma-auth") : null;

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges session_id first, so skip /me here
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    getMe()
      .then((data) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  // Listen for auth expiry from axios interceptor
  useEffect(() => {
    const handleExpiry = () => { setUser(null); toast.error("Your session ended. Please sign in again."); };
    window.addEventListener("auth-expired", handleExpiry);
    if (authChannel) authChannel.onmessage = () => window.location.reload();
    return () => window.removeEventListener("auth-expired", handleExpiry);
  }, []);

  const handleAuth = useCallback((data) => {
    setUser(data);
    authChannel?.postMessage("changed");
  }, []);

  const handleLogout = useCallback(async () => {
    try { await logoutUser(); } catch (err) { console.error("Logout error:", err); }
    setUser(null);
    authChannel?.postMessage("changed");
  }, []);

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50" style={{ minHeight: '100dvh' }}>
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="App">
      <AuthContext.Provider value={user}>
        <BrowserRouter>
          <AppRoutes user={user} onAuth={handleAuth} onLogout={handleLogout} />
        </BrowserRouter>
      </AuthContext.Provider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
